import React, { useState } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  FormField,
  Input,
  Textarea,
  Button,
  Header,
  Container,
  KeyValuePairs,
  Alert,
} from '@amzn/awsui-components-react-v3';
import { Report, StartDiffReportGenerationInput, StartDiffReportGenerationOutput } from 'aws-sdk/clients/d3aservice';
import { startDiffReportGeneration } from 'src/api/d3a';
import { activeGroupOrWorkspaceId } from 'src/components/dataadvisory/constants';
import { createReportDetailLink } from 'src/routes';

export interface CompareReportsModalProps {
  visible: boolean;
  onDismiss: () => void;
  selectedReports: Report[];
  activeGroup: string;
  activeWorkspace?: any;
  setNotifications?: any;
  setRedirect?: any;
}

export const CompareReportsModal = (props: CompareReportsModalProps) => {
  const [diffReportName, setDiffReportName] = useState('');
  const [diffReportDescription, setDiffReportDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [apiError, setApiError] = useState('');

  const report1 = props.selectedReports?.[0];
  const report2 = props.selectedReports?.[1];

  // Don't render if we don't have exactly 2 reports
  if (!report1 || !report2) {
    return null;
  }

  const handleGenerateDiff = async () => {
    // Validate inputs
    let hasError = false;
    if (!diffReportName || diffReportName.trim() === '') {
      setNameError('Report name is required');
      hasError = true;
    } else {
      setNameError('');
    }

    if (!diffReportDescription || diffReportDescription.trim() === '') {
      setDescriptionError('Report description is required');
      hasError = true;
    } else {
      setDescriptionError('');
    }

    if (hasError) return;

    setLoading(true);
    setApiError(''); // Clear previous errors
    try {
      // Create diff report using startDiffReportGeneration
      const startDiffReportInput: StartDiffReportGenerationInput = {
        diffReportConfig: {
          reportId1: report1.reportId,
          reportId2: report2.reportId,
        },
        name: diffReportName,
        description: diffReportDescription,
        ownerId: activeGroupOrWorkspaceId({ activeGroup: props.activeGroup, activeWorkspace: props.activeWorkspace }),
      };

      const response: StartDiffReportGenerationOutput = await startDiffReportGeneration(startDiffReportInput);

      props.setNotifications?.([
        {
          type: 'success',
          content: `Diff report generation started successfully. Report ID: ${response.diffReportId}`,
          dismissible: true,
          onDismiss: () => props.setNotifications([]),
        },
      ]);

      // Reset and close modal
      setDiffReportName('');
      setDiffReportDescription('');
      props.onDismiss();

      // Redirect to diff report detail page after 1 second
      setTimeout(() => {
        props.setNotifications?.([]);
        props.setRedirect?.(createReportDetailLink(response.diffReportId));
      }, 1000);
    } catch (e: any) {
      console.error('Failed to generate diff report:', e);
      const errorMessage = e?.message || e?.toString() || 'Unknown error occurred';
      setApiError(errorMessage);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    setDiffReportName('');
    setDiffReportDescription('');
    setNameError('');
    setDescriptionError('');
    setApiError('');
    props.onDismiss();
  };

  return (
    <Modal
      visible={props.visible}
      onDismiss={handleDismiss}
      header={<Header variant='h2'>Compare reports</Header>}
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={handleDismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleGenerateDiff} loading={loading}>
              Generate diff report
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='large'
    >
      <SpaceBetween size='l'>
        {apiError && (
          <Alert type='error' dismissible onDismiss={() => setApiError('')}>
            {apiError}
          </Alert>
        )}
        <Container header={<Header variant='h3'>Selected reports</Header>}>
          <SpaceBetween size='m'>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <Container header={<Header variant='h4'>Report 1</Header>}>
                <KeyValuePairs
                  columns={1}
                  items={[
                    {
                      label: 'ID',
                      value: report1?.reportId || '-',
                    },
                    {
                      label: 'Name',
                      value: report1?.name || '-',
                    },
                    {
                      label: 'Created On',
                      value: report1?.createdOn || '-',
                    },
                  ]}
                />
              </Container>
              <Container header={<Header variant='h4'>Report 2</Header>}>
                <KeyValuePairs
                  columns={1}
                  items={[
                    {
                      label: 'ID',
                      value: report2?.reportId || '-',
                    },
                    {
                      label: 'Name',
                      value: report2?.name || '-',
                    },
                    {
                      label: 'Created On',
                      value: report2?.createdOn || '-',
                    },
                  ]}
                />
              </Container>
            </div>
          </SpaceBetween>
        </Container>

        <Container header={<Header variant='h3'>Diff report details</Header>}>
          <SpaceBetween size='m'>
            <FormField label='Report name' errorText={nameError}>
              <Input
                value={diffReportName}
                onChange={({ detail }) => {
                  setDiffReportName(detail.value);
                  if (detail.value.trim()) setNameError('');
                }}
                placeholder='Enter diff report name'
              />
            </FormField>

            <FormField label='Report description' errorText={descriptionError}>
              <Textarea
                value={diffReportDescription}
                onChange={({ detail }) => {
                  setDiffReportDescription(detail.value);
                  if (detail.value.trim()) setDescriptionError('');
                }}
                placeholder='Enter diff report description'
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Modal>
  );
};
